import React, { useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem,
    Select, Switch, TextField, Grid
} from '@material-ui/core';
import { AdminPackageRequest } from 'api/whatsapp/types';
import { IApiState } from 'store/types';
import { apiStatus } from 'shared/constants/AppEnum';

interface PackageFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: AdminPackageRequest) => void;
    api: IApiState;
    initialData?: AdminPackageRequest & { id?: number };
    mode: 'create' | 'edit';
}

const defaultValues: AdminPackageRequest = {
    name: '',
    package_type: 'transactional',
    price: 0,
    message_count: 0,
    description: '',
    is_active: true,
    display_order: 0,
};

const PackageFormDialog: React.FC<PackageFormDialogProps> = ({
    open, onClose, onSubmit, api, initialData, mode
}) => {
    const [form, setForm] = useState<AdminPackageRequest>(initialData || defaultValues);
    const isLoading = api.status === apiStatus.LOADING;

    React.useEffect(() => {
        if (open) {
            setForm(initialData || defaultValues);
        }
    }, [open, initialData]);

    const handleChange = (field: keyof AdminPackageRequest, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSubmit(form);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{mode === 'create' ? 'Create Package' : 'Edit Package'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Package Name"
                            value={form.name}
                            onChange={e => handleChange('name', e.target.value)}
                            fullWidth
                            required
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Package Type</InputLabel>
                            <Select
                                value={form.package_type}
                                onChange={e => handleChange('package_type', e.target.value)}
                            >
                                <MenuItem value="transactional">Transactional</MenuItem>
                                <MenuItem value="promotional">Promotional</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Price (₹)"
                            type="number"
                            value={form.price}
                            onChange={e => handleChange('price', parseFloat(e.target.value))}
                            fullWidth
                            required
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Message Count"
                            type="number"
                            value={form.message_count}
                            onChange={e => handleChange('message_count', parseInt(e.target.value))}
                            fullWidth
                            required
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Display Order"
                            type="number"
                            value={form.display_order ?? 0}
                            onChange={e => handleChange('display_order', parseInt(e.target.value))}
                            fullWidth
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            value={form.description ?? ''}
                            onChange={e => handleChange('description', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_active ?? true}
                                    onChange={e => handleChange('is_active', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !form.name || !form.price || !form.message_count}
                    startIcon={isLoading ? <CircularProgress size={14} /> : undefined}
                >
                    {mode === 'create' ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PackageFormDialog;
